// src/components/CrewForm.jsx
import React, { useCallback, useEffect, useState } from 'react';
import ReusableButton from './Button/Button';
import { IStaffAllocation } from '../../type/staffAllocation';
import { IBalloonDetails } from '../../type/balloonDetails';
import operationAPI from '../../api/operationApi';
import { ICrewDetails } from '../../type/crew';
import { IBusDetails } from '../../type/busDetails';
import { useForm, SubmitHandler } from 'react-hook-form';
import { IAddCrewRequest } from '../../type/addCrew';
import useLocationStore from '../../store/locatonStore';
import { StaffConstant } from '../../constants/constant';
import CheckIcon from '../../assets/CheckIcon';
import CrossIcon from '../../assets/CrossIcon';
import { toast } from 'react-toastify';
import { translate } from '../../i18n';
import { encodeDateForUrl } from '../../utils/dateUtils';
import RedCrossIcon from '../../assets/icon/RedCrossIcon';
import CrossIconDarkForm from '../../assets/icon/CrossIconForm';
interface CrewFormProps {
  heading: string;
  closeModal: () => void;
  crewList: IStaffAllocation[];
  balloons: IBalloonDetails[];
  crewVehicleList: IBusDetails[];
  operationStaffId?: number;
  initialValues?: IStaffAllocation;
  onStaffChange: () => void;
  staffAllocation?: IStaffAllocation[] | null;
  date: Date;
}

const CrewForm: React.FC<CrewFormProps> = ({
  heading,
  closeModal,
  crewList,
  balloons,
  crewVehicleList,
  operationStaffId,
  initialValues,
  onStaffChange,
  staffAllocation,
  date,
}) => {
  const {
    setValue,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IAddCrewRequest>();

  const { selectedLocation } = useLocationStore();
  const [maxPos, setMaxPos] = useState<number>();
  const [crewLists, setCrewList] = useState<ICrewDetails[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [addAnother, setAddAnother] = useState(false);
  const [selectedBalloon, setSelectedBalloon] =
    useState<IBalloonDetails | null>(null);

  function sanitizeData(data: any): any {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) =>
        value === '' ? [key, null] : [key, value],
      ),
    );
  }

  const fetchStaffList = useCallback(async () => {
    try {
      const response = await operationAPI.crewList(
        selectedLocation.OwnerId,
        encodeDateForUrl(date),
      );
      const crewList: ICrewDetails[] = [];
      response?.data?.map((crew: ICrewDetails) => {
        crewList.push(crew);
      });

      if (initialValues?.staffId) {
        const alreadyExists = crewList.some(
          (crew) => crew.staffId === initialValues.staffId,
        );

        if (!alreadyExists) {
          const staffFromInitialValues = response?.data?.find(
            (driver: ICrewDetails) => driver.staffId === initialValues.staffId,
          );
          if (staffFromInitialValues) {
            crewList.push(staffFromInitialValues);
          }
        }
      }

      setCrewList(crewList);
    } catch (error) {
      console.log(error);
    }
  }, [initialValues, crewList]);

  useEffect(() => {
    fetchStaffList();
    const pos = getMaxPos(staffAllocation ?? []);
    setMaxPos(pos + 1);
  }, []);
  const handleBalloonChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const balloonId = parseInt(event.target.value);
    const balloon = balloons.find((b) => b.balloonId === balloonId) || null;
    setSelectedBalloon(balloon);
  };

  const getMaxPos = (staffAllocation: IStaffAllocation[]): number => {
    if (staffAllocation.length === 0) return 0;
    return Math.max(...staffAllocation.map((staff) => staff.pos));
  };

  const onSubmit: SubmitHandler<IAddCrewRequest> = async (data) => {
    setIsLoading(true);
    if (operationStaffId||initialValues?.balloonId||initialValues?.busId) {
      try {
        const sanitizedDatas = sanitizeData({
          ...data,
          ownerId: selectedLocation.OwnerId,
          roleId: StaffConstant.CREW,
          date: encodeDateForUrl(date),
          operationStaffId: operationStaffId,
        });
        await operationAPI.updateCrew(sanitizedDatas);
        setIsLoading(false);
        toast.success(translate('crew.crewUpdated'));
        if (addAnother) {
          onStaffChange();
          setIsLoading(false);
          reset();
          setSelectedStaffId(undefined);
        } else {
          setTimeout(() => {
            closeModal();
            onStaffChange();
          }, 500);
        }
      } catch (error) {
        console.error('Failed to update crew:', error);
        toast.warn(translate('operation.commonError'));
      }
    } else {
      try {
        const sanitizedData = sanitizeData({
          ...data,
          ownerId: selectedLocation.OwnerId,
          roleId: StaffConstant.CREW,
          date: encodeDateForUrl(date),
          pos: maxPos,
        });
        await operationAPI.addCrew({
          ...sanitizedData,
          qualification: data.qualification,
        });
        setIsLoading(false);
        toast.success(translate('crew.crewAdded'));
        if (addAnother) {
          reset();
          setIsLoading(false);
          onStaffChange();
          setSelectedStaffId(undefined);
          setSelectedBalloon(null);
          setValue('busId', 0);
        } else {
          setTimeout(() => {
            closeModal();
            onStaffChange();
          }, 500);
        }
      } catch (error) {
        setIsLoading(false);
        console.error('Failed to add crew:', error);
        toast.warn(translate('operation.commonError'));
      }
    }
  };
  const [selectedStaffId, setSelectedStaffId] = useState<number>();
  useEffect(() => {
    if (initialValues) {
      setValue('staffId', initialValues?.staffId);
      setValue('balloonId', initialValues?.balloonId);
      setValue('busId', initialValues?.busId);
      setSelectedBalloon(
        balloons.find(
          (balloon) => balloon.balloonId == initialValues.balloonId,
        ) || null,
      );
      if (!selectedStaffId) {
        setSelectedStaffId(initialValues?.staffId);
      }
    } else {
      // setValue('qualification', '');
    }
  }, [initialValues, setValue]);

  return (
    <>
      <div className="max-w-md mx-auto   bg-sidebar-background p-6 rounded-lg shadow-md relative">
        {/* Title and Close Button */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-textColor">{heading}</h2>
          <button
            className="text-textColorGray hover:text-textColorGray"
            onClick={closeModal}
          >
            {/* Close Icon */}
            <RedCrossIcon />
          </button>
        </div>
        <div className="border-b-2 border-[#EEF7FF] mb-4"></div>
        <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
          <div className="overflow-y-auto max-h-[calc(100vh-100px)] min-w-full grid grid-cols-2 gap-4">
            {/* Crew Name */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('crew.crewName')}
              </label>

              <select
                {...register('staffId', {
                  onChange: (e) => {
                    const value = e.target.value;
                    setSelectedStaffId(value);
                    setValue('staffId', parseInt(value));
                    const selectedDriver = crewLists?.find(
                      (driver) => driver?.staffId === Number(value),
                    );
                    if (selectedDriver) {
                      setValue('phoneNumber', selectedDriver?.staffContact);
                    }
                    if (selectedDriver) {
                      setValue(
                        'qualification',
                        selectedDriver?.staffqualifications,
                      );
                    }
                  },
                })}
                value={selectedStaffId}
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
              >
                {crewLists && crewLists.length > 0 ? (
                  <>
                    <option value="">{translate('crew.crewSelect')}</option>
                    {crewLists.map((pilot: ICrewDetails) => (
                      <option value={pilot.staffId} key={pilot.staffId}>
                        {pilot.staffName}
                      </option>
                    ))}
                  </>
                ) : null}
              </select>

              {/* Display error message */}
              {errors.staffId && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.staffId.message}
                </p>
              )}
            </div>

            {/* Balloon */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('driver.balloon')}
              </label>
              <select
                {...register('balloonId')}
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                onChange={handleBalloonChange}
                value={selectedBalloon?.balloonId || ''}
                defaultValue={initialValues?.operationBalloonId}
              >
                <option value="">{translate('driver.selectBalloon')}</option>
                {balloons?.map((balloon) => (
                  <option value={balloon?.balloonId} key={balloon?.balloonId}>
                    {
                      balloon?.formattedBalloonName
                    }
                  </option>
                ))}
              </select>
              {errors.balloonId && (
                <span className="text-red-500">
                  {translate('driver.balloonValidation')}
                </span>
              )}
            </div>

            {/* Qualification */}
            <div className="col-span-2 mt-4">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('crew.qualification')}
              </label>
              <input
                type="text"
                {...register('qualification')}
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                defaultValue={
                  initialValues?.staffId
                    ? `${initialValues?.staffqualifications}`
                    : ''
                }
                disabled
              />
            </div>

            {/* Vehicle */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('crew.vehicle')}
              </label>
              <select
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                {...register('busId')}
              >
                <option value="" disabled selected>
                  {translate('crew.vehicleSelection')}
                </option>
                {crewVehicleList?.map((crewVehicle) => (
                  <option key={crewVehicle?.busId} value={crewVehicle?.busId}>
                    {crewVehicle?.busName}
                  </option>
                ))}
              </select>
              {errors.busId && (
                <span className="text-red-500">{errors.busId.message}</span>
              )}
            </div>

            {/* Phone Number */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start ">
                {translate('crew.phoneNumber')}
              </label>
              <input
                type="text"
                placeholder={translate('crew.phoneNumberPlaceholder')}
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                defaultValue={initialValues?.resPhone.replace(
                  /^[^\d]*(\d.*)/,
                  '$1',
                )}
                readOnly
              />
            </div>

            {/* Start Odo */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('driver.startOdo')}
              </label>
              <input
                type="text"
                placeholder={translate('driver.startOdoPlaceholder')}
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                {...register('startOdo')}
                defaultValue={initialValues?.startOdo ?? ''}
              />
              {errors.startOdo && (
                <span className="text-red-500">{errors.startOdo.message}</span>
              )}
            </div>

            {/* End Odo */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('driver.endOdo')}
              </label>
              <input
                type="text"
                placeholder={translate('driver.endOdoPlaceHolder')}
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                {...register('endOdo', {})}
                defaultValue={initialValues?.endOdo ?? ''}
              />
              {errors.endOdo && (
                <span className="text-red-500">{errors.endOdo.message}</span>
              )}
            </div>

            {/* Fuel */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-textColorGray text-start">
                {translate('driver.fuel')}
              </label>
              <input
                type="text"
                placeholder={translate('driver.fuelPlaceholder')}
                className="mt-1 block w-full p-2 border border-customBorder-border rounded-md  bg-backgroundColor-darkThemeColor text-text-secondary"
                {...register('fuel')}
                defaultValue={initialValues?.fuel ?? ''}
              />
              {errors.fuel && (
                <span className="text-red-400">{errors.fuel.message}</span>
              )}
            </div>

            {/* Checkboxes */}
            <div className="col-span-2 flex flex-col gap-2">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  className="w-5 h-5 mr-2 text-textColor"
                  {...register('need')}
                  defaultChecked={initialValues?.need}
                />
                <span className="text-sm text-textColorGray">
                  {translate('operationCommon.need')}
                </span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  className="w-5 h-5 mr-2 text-textColor"
                  {...register('kkd')}
                  defaultChecked={initialValues?.kkd}
                />
                <span className="text-sm text-textColorGray">
                  {translate('operationCommon.kkd')}
                </span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  className="w-5 h-5 mr-2 text-textColor"
                  {...register('preCheck')}
                  defaultChecked={initialValues?.preCheck}
                />
                <span className="text-sm text-textColorGray">
                  {translate('operationCommon.preCheck')}
                </span>
              </label>
            </div>
          </div>
          <div className="col-span-2 flex mt-4">
            <div className="flex w-auto items-center space-x-2">
              <input
                type="checkbox"
                id="addAnother"
                className="w-5 h-5 border-light-blue"
                checked={addAnother}
                onChange={(e) => setAddAnother(e.target.checked)}
              />
              <label
                htmlFor="addAnother"
                className="text-sm whitespace-nowrap mb-1 text-textColorGray"
              >
                {translate('common.addAnother')}
              </label>
            </div>
            <div className="flex w-full justify-end">
              <ReusableButton
                variant="secondary"
                children={'Cancel'}
                icon={
                  <div>
                    <div className="block dark:hidden">
                      <CrossIcon />
                    </div>
                    <div className="hidden dark:block">
                      <CrossIconDarkForm />
                    </div>
                  </div>
                }
                onClick={closeModal}
                isDisabled={isLoading}
                className="w-auto bg-button-cancelButtonBgColor text-text-cancelButtonTextColor"
              />
              <ReusableButton
                icon={<CheckIcon />}
                children={'Confirm'}
                variant="primary"
                className="w-auto bg-background-backgroundColor"
                isDisabled={isLoading}
                isLoader={isLoading}
              />
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default CrewForm;
